import { useEffect, useRef, useState } from 'react';

import { appParams } from '@playbooks/api';
import { ThumbnailPhotoLinkCard } from '@playbooks/components/thumbnail/thumbnail-card';
import { useStore } from '@playbooks/contexts';
import { useAction, useInit } from '@playbooks/hooks';
import { SearchForm } from '@playbooks/molecules/forms';
import { PhotoList } from '@playbooks/molecules/lists';
import { Pagination } from '@playbooks/molecules/paginations';
import { ViewSubnav } from '@playbooks/molecules/subnavs';
import { Button } from '@playbooks/ui/components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { FilestackThumbnail } from '@playbooks/wrappers/filestack';

const ThumbnailSelectModal = ({ model, open, setOpen, saveApi, onNext }) => {
	const [selected, setSelected] = useState<any>({});
	const [thumbnails, setThumbnails] = useState([]);
	const [params, setParams] = useState<any>({ ...appParams, pageSize: 6 });
	const [meta, setMeta] = useState({ page: 0, pageSize: 0, totalRecords: 0 });
	const [tableName, setTableName] = useState('');
	const ref = useRef(null);
	const store = useStore();

	// Computed
	const tableNames = ['categories', 'projects', 'templates'];
	const filteredThumbnails = thumbnails.filter(v => v.thumbnail?.includes('https'));

	// Hooks
	useEffect(() => {
		if (open) fetchData();
	}, [open, params]);
	useEffect(() => {
		if (open) {
			setSelected(model || {});
			setTimeout(() => ref.current?.focus(), 300);
		}
	}, [open]);
	useEffect(() => {
		switch (tableName) {
			case '':
				return setParams({ ...appParams, pageSize: 6 });
			default:
				return setParams({ ...appParams, pageSize: 18 });
		}
	}, [tableName]);

	// Actions
	const [fetchData, loading] = useInit(async () => {
		tableName !== '' ? await searchTable() : await searchTables();
	});
	const [onSave, saveTask] = useAction(async () => {
		onNext(selected.thumbnail);
	});

	// Methods
	const searchTable = async () => {
		const response = await store.request({ url: '/search-table', params: { ...params, tableName } });
		setThumbnails(response.data);
		setMeta(response.meta);
	};
	const searchTables = async () => {
		const response = await store.request({ url: '/search-tables', params: { ...params, tableNames } });
		setThumbnails(response.map(record => record.data).flat());
	};
	const onSelect = value => {
		setSelected(selected.id !== value.id ? value : {});
	};
	const onClose = () => setOpen(false);
	const onSubmit = e => {
		e.preventDefault();
		onSave();
	};

	// Render
	return (
		<Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
			<DialogContent className='max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-lg'>
				<form onSubmit={onSubmit}>
					<DialogHeader>
						<DialogTitle>Select Thumbnail</DialogTitle>
					</DialogHeader>

					<div className='border-b'>
						<SearchForm
							id='thumbnail_search'
							ref={ref}
							placeholder='Search thumbnails...'
							query={params.query}
							setQuery={query => setParams({ ...params, page: 0, query })}
							size='xs'
							className='grow'
						/>
					</div>

					<div className='border-b px-4'>
						<ViewSubnav view={tableName} options={['', 'categories', 'projects', 'templates']} setView={setTableName} />
					</div>

					<div>
						<PhotoList layout='photo' data={filteredThumbnails} loading={loading} icon='images' title='Thumbnails'>
							{filteredThumbnails.map((thumbnail, index) => (
								<div key={index} className='col-span-4 md:col-span-2'>
									<ThumbnailPhotoLinkCard
										thumbnail={thumbnail}
										selected={selected}
										loading={loading}
										onClick={onSelect}
									/>
								</div>
							))}
							<div className='col-span-4 md:col-span-2'>
								<FilestackThumbnail value={selected.thumbnail} onChange={file => onNext(file.url)} className='w-full' />
							</div>
						</PhotoList>
					</div>

					<div className='flex-between'>
						<Pagination meta={meta} params={params} setParams={setParams} />
					</div>

					<DialogFooter>
						<Button variant='secondary' onClick={onClose}>
							Close
						</Button>
						<Button type='submit' isLoading={saveTask.running}>
							Save
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};
export { ThumbnailSelectModal };
